import React from 'react';
import Products2Hero from '../components/Products2Hero';
import './WarrantyPage.css';

const WarrantyPage = () => {
  const sections = [
    { id: 'sec-1', num: '01', title: 'Introduction' },
    { id: 'sec-2', num: '02', title: 'Warranty Duration' },
    { id: 'sec-3', num: '03', title: "What's Covered" },
    { id: 'sec-4', num: '04', title: "What's Not Covered" },
    { id: 'sec-5', num: '05', title: 'Customer Cooperation' },
    { id: 'sec-6', num: '06', title: 'Resolution Process' },
    { id: 'sec-7', num: '07', title: 'Shipping & Handling' },
    { id: 'sec-8', num: '08', title: 'Limitation of Liability' },
    { id: 'sec-9', num: '09', title: 'Proof of Purchase' },
    { id: 'sec-10', num: '10', title: 'Governing Law' },
    { id: 'sec-11', num: '11', title: 'Policy Updates' },
  ];

  return (
    <div className="warranty-page">
      <Products2Hero 
        className="hero-tight"
        tagline="CERTIFIED QUALITY · 12-MONTH COVERAGE · LIFETIME SUPPORT"
        title1="Warranty Policy"
        title2=""
        title3=""
        description=""
        button1Text="Claim Warranty"
        button1Link="/help-center#contact-form"
        button2Text="Full Policy"
        button2Link="#warranty-content"
      />

      <div id="warranty-content" className="warranty-content">
        {/* Sticky Sidebar Navigation */}
        <aside className="warranty-sidebar">
          <h4 className="sidebar-title">Quick Navigation</h4>
          <ul className="sidebar-nav">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>
                  <span className="sidebar-nav-num">{s.num}</span>
                  <span>{s.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </aside>

        {/* Main Sections */}
        <div className="warranty-sections">
          {/* Introduction */}
          <section id="sec-1" className="warranty-section">
            <h2 className="section-title">Introduction</h2>
            <p className="section-text">
              At Nexawave Software Solutions Private Limited, we are committed to delivering carefully tested, high-quality refurbished laptops and electronic devices. Our warranty policy ensures a smooth and transparent experience for all our customers.
            </p>
            <div className="note-box">
              <p><strong>Note:</strong> This policy applies only to purchases made directly from Nexawave Software Solutions Private Limited (in-store or through our official website). Purchases via third-party platforms or resellers are subject to their respective warranty terms.</p>
            </div>
          </section>

          {/* Warranty Duration */}
          <section id="sec-2" className="warranty-section">
            <h2 className="section-title">Warranty Duration</h2>
            <h3 className="subsection-title">Standard Coverage</h3>
            <p className="section-text">All refurbished products include a 1-year limited warranty from the date of delivery.</p>
            <h3 className="subsection-title">Extended Warranty</h3>
            <p className="section-text">Customers may purchase extended warranty coverage of up to 2 years, if offered at the time of purchase.</p>
          </section>

          {/* What's Covered */}
          <section id="sec-3" className="warranty-section">
            <h2 className="section-title">What's Covered</h2>
            <p className="section-text">This warranty covers hardware defects and functional failures under normal usage conditions:</p>
            <ul className="coverage-list">
              <li>Screen/display issues</li>
              <li>Motherboard or processor failure</li>
              <li>Keyboard, touchpad, or other input device malfunctions</li>
              <li>RAM or hard drive defects</li>
              <li>Battery issues (if degradation due to manufacturing defects occurs within 6 months)</li>
              <li>Included accessories (charger, adapter) – 6-month warranty</li>
            </ul>
          </section>

          {/* What's Not Covered */}
          <section id="sec-4" className="warranty-section not-covered">
            <h2 className="section-title">What's Not Covered</h2>
            <p className="section-text">The following are not covered under this warranty:</p>
            <ul className="coverage-list">
              <li>Software-related issues, operating system errors, or third-party apps</li>
              <li>Physical damage, liquid damage, or accidental breakage</li>
              <li>Battery degradation due to regular usage (after 6 months)</li>
              <li>Electrical surges or damage caused by third-party accessories</li>
              <li>Unauthorized repairs, tampering, or modifications</li>
              <li>Devices with removed, altered, or tampered serial numbers</li>
            </ul>
          </section>

          {/* Customer Cooperation */}
          <section id="sec-5" className="warranty-section">
            <h2 className="section-title">Customer Cooperation & Troubleshooting Requirement</h2>
            <p className="section-text">Before proceeding with a replacement or refund, customer cooperation is mandatory:</p>
            <ul className="process-list">
              <li>Our support team will respond within 24–48 business hours.</li>
              <li>You will be guided through remote troubleshooting via call, chat, or video.</li>
              <li>Many issues are resolved without the need to return the product.</li>
            </ul>
            <div className="note-box">
              <p><strong>Note:</strong> Requests for direct replacement or refund without completing troubleshooting will not be accepted.</p>
            </div>
          </section>

          {/* Resolution Process */}
          <section id="sec-6" className="warranty-section">
            <h2 className="section-title">Resolution Process</h2>
            <p className="section-text">After successful troubleshooting:</p>
            <ul className="process-list">
              <li><strong>Repair:</strong> The product may be picked up and repaired at our facility.</li>
              <li><strong>Replacement:</strong> If the item is not repairable, a similar or higher specification replacement will be provided.</li>
              <li><strong>Refund:</strong> In unresolved cases, or if the product is not repairable and no suitable replacement is available, a refund may be issued after inspection (subject to depreciation if applicable).</li>
            </ul>
          </section>

          {/* Shipping & Handling */}
          <section id="sec-7" className="warranty-section">
            <h2 className="section-title">Shipping & Handling</h2>
            <div className="shipping-table">
              <div className="table-header">
                <div className="table-cell">Warranty Period</div>
                <div className="table-cell">Shipping Responsibility</div>
              </div>
              <div className="table-row">
                <div className="table-cell">0–14 Days</div>
                <div className="table-cell">Nexawave covers both sides</div>
              </div>
              <div className="table-row">
                <div className="table-cell">15 Days – 6 Months</div>
                <div className="table-cell">One side by customer, one by Nexawave</div>
              </div>
              <div className="table-row">
                <div className="table-cell">6–12 Months</div>
                <div className="table-cell">Customer covers both sides (max ₹600)</div>
              </div>
            </div>
            <p className="section-text">Warranty service is free, but shipping charges apply as per the table above.</p>
            <p className="section-text">For ineligible claims (e.g., damage due to misuse), diagnostic and shipping charges may apply.</p>

            <h3 className="subsection-title">Important: Secure Packaging</h3>
            <p className="section-text">Customers must package products securely when returning. Damage in transit due to poor packaging is not Nexawave's responsibility.</p>

            <h3 className="subsection-title">Lost or Tampered Packages During Pickup</h3>
            <p className="section-text">If a product gets lost or tampered with during pickup arranged by Nexawave:</p>
            <ul className="process-list">
              <li>A 50% reimbursement of the product value will be processed.</li>
              <li>Refunds will be completed within 7–14 business days after verification.</li>
              <li>You must provide a pickup receipt and all required documentation.</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section id="sec-8" className="warranty-section">
            <h2 className="section-title">Limitation of Liability</h2>
            <p className="section-text">Nexawave Software Solutions Private Limited is not responsible for:</p>
            <ul className="coverage-list">
              <li>Loss of personal data stored on the device</li>
              <li>Business interruption or loss of productivity</li>
              <li>Commercial, indirect, or consequential damages</li>
            </ul>
            <p className="section-text">Our total liability is limited to repair, replacement, or refund, as outlined above.</p>
          </section>

          {/* Proof of Purchase */}
          <section id="sec-9" className="warranty-section">
            <h2 className="section-title">Proof of Purchase & Ownership</h2>
            <p className="section-text">To claim warranty, the customer must:</p>
            <ul className="process-list">
              <li>Provide a valid invoice or order confirmation</li>
              <li>Be able to verify ownership (especially for locked or password-protected devices)</li>
            </ul>
          </section>

          {/* Governing Law */}
          <section id="sec-10" className="warranty-section">
            <h2 className="section-title">Governing Law</h2>
            <p className="section-text">This Warranty Policy is governed by the laws of India, and any disputes shall fall under the jurisdiction of Hyderabad, Telangana courts.</p>
          </section>

          {/* Policy Updates */}
          <section id="sec-11" className="warranty-section">
            <h2 className="section-title">Policy Updates</h2>
            <p className="section-text">Nexawave Software Solutions Private Limited reserves the right to update or revise this policy without prior notice. Please refer to our official website for the latest version of this Warranty Policy.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WarrantyPage;